import { useCallback, useState } from "react";
import { nsKey } from "./partnerStorage";

/**
 * "Ignore" preference for the AI suggestion panels that ride along on the
 * Exceptions and Conditions tabs.
 *
 * It is a per-USER, cross-LOAN preference — dismissing the panel on one loan
 * hides it on every loan — so it lives in localStorage rather than on the deal.
 * Partner-scoped through `nsKey` so builds sharing localhost:3000 don't share
 * the flag. The AI Review tab reads the same flag to decide whether to offer a
 * "Show in … tab" button, which is the only way back.
 */
export const AI_SUGGESTION_SCOPES = {
  EXCEPTIONS: "exceptions",
  CONDITIONS: "conditions",
};

const STORAGE_KEYS = {
  [AI_SUGGESTION_SCOPES.EXCEPTIONS]: nsKey("aiSuggestedExceptionsIgnored"),
  [AI_SUGGESTION_SCOPES.CONDITIONS]: nsKey("aiSuggestedConditionsIgnored"),
};

export const isAiSuggestionIgnored = (scope) => {
  try {
    return window.localStorage.getItem(STORAGE_KEYS[scope]) === "1";
  } catch {
    // Private mode / blocked storage — treat as "not ignored".
    return false;
  }
};

const writeIgnored = (scope, ignored) => {
  try {
    if (ignored) window.localStorage.setItem(STORAGE_KEYS[scope], "1");
    else window.localStorage.removeItem(STORAGE_KEYS[scope]);
  } catch {
    // Nothing to persist to — the in-memory state below still applies for
    // this session.
  }
};

/**
 * @param {string} scope one of AI_SUGGESTION_SCOPES
 * @returns {{ ignored: boolean, ignore: () => void, show: () => void }}
 */
export const useAiSuggestionIgnored = (scope) => {
  const [ignored, setIgnored] = useState(() => isAiSuggestionIgnored(scope));

  const ignore = useCallback(() => {
    writeIgnored(scope, true);
    setIgnored(true);
  }, [scope]);

  const show = useCallback(() => {
    writeIgnored(scope, false);
    setIgnored(false);
  }, [scope]);

  return { ignored, ignore, show };
};
